import { Input, Select, Textarea } from '../ui/Input';
import { BranchMapPreview } from './BranchMapPreview';

// ── Saat dilimi listesi (IANA) ────────────────────────────────────────
const COMMON_TZ = [
  'Europe/Istanbul',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Moscow',
  'America/New_York',
  'America/Los_Angeles',
  'Asia/Dubai',
];
const ALL_TZ = (typeof Intl.supportedValuesOf === 'function'
  ? Intl.supportedValuesOf('timeZone')
  : COMMON_TZ
).filter((tz) => !COMMON_TZ.includes(tz));

// Şube kodu — T2 backend regex'i ile aynı.
const CODE_REGEX = /^[A-Za-z0-9_-]{0,20}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ── Form yardımcıları ─────────────────────────────────────────────────

/** Create modu için boş form state. */
export const EMPTY_BRANCH_FORM = {
  name: '', code: '',
  address: '', city: '', district: '', postal_code: '', country_code: 'TR',
  latitude: '', longitude: '', geo_radius_meters: '100',
  timezone: 'Europe/Istanbul',
  manager_user_id: '',
  phone: '', email: '',
};

/** Backend'den gelen branch'i form state'ine çevirir (edit modu). */
export function branchToForm(branch) {
  return {
    name: branch.name || '',
    code: branch.code || '',
    address: branch.address || '',
    city: branch.city || '',
    district: branch.district || '',
    postal_code: branch.postal_code || '',
    country_code: branch.country_code || 'TR',
    latitude: branch.latitude != null ? String(branch.latitude) : '',
    longitude: branch.longitude != null ? String(branch.longitude) : '',
    geo_radius_meters: branch.geo_radius_meters != null ? String(branch.geo_radius_meters) : '100',
    timezone: branch.timezone || 'Europe/Istanbul',
    manager_user_id: branch.manager_user_id || '',
    phone: branch.phone || '',
    email: branch.email || '',
  };
}

/**
 * Client-side doğrulama (ilk hat — backend authoritative).
 * Alan-bazlı hata objesi döner; boş obje = geçerli.
 */
export function validateBranchForm(form) {
  const errors = {};

  const name = (form.name || '').trim();
  if (!name) errors.name = 'Şube adı zorunludur';
  else if (name.length < 2 || name.length > 120) errors.name = 'Şube adı 2-120 karakter olmalıdır';

  const code = (form.code || '').trim();
  if (code && !CODE_REGEX.test(code)) {
    errors.code = 'Kod en fazla 20 karakter; yalnız harf, rakam, tire, alt çizgi';
  }

  // latitude/longitude paired — ikisi dolu VEYA ikisi boş.
  const hasLat = form.latitude !== '' && form.latitude != null;
  const hasLng = form.longitude !== '' && form.longitude != null;
  if (hasLat !== hasLng) {
    errors.latitude = 'Enlem ve boylam birlikte girilmelidir';
  } else if (hasLat) {
    const lat = Number(form.latitude);
    const lng = Number(form.longitude);
    if (!Number.isFinite(lat) || lat < -90 || lat > 90) {
      errors.latitude = 'Enlem -90 ile 90 arasında olmalıdır';
    }
    if (!Number.isFinite(lng) || lng < -180 || lng > 180) {
      errors.longitude = 'Boylam -180 ile 180 arasında olmalıdır';
    }
  }

  if (form.geo_radius_meters !== '' && form.geo_radius_meters != null) {
    const r = Number(form.geo_radius_meters);
    if (!Number.isFinite(r) || r < 10 || r > 5000) {
      errors.geo_radius_meters = 'Yarıçap 10-5000 metre arasında olmalıdır';
    }
  }

  const email = (form.email || '').trim();
  if (email && !EMAIL_REGEX.test(email)) {
    errors.email = 'Geçerli bir email adresi giriniz';
  }

  return errors;
}

/**
 * Form state'ini API payload'ına çevirir.
 * ⚠ Nullable alanlar boş bırakılırsa "" DEĞİL null gönderilir (T2 backend notu —
 * boş string format validator'a takılır, 400 verir).
 */
export function buildBranchPayload(form) {
  const text = (v) => {
    const s = (v ?? '').toString().trim();
    return s === '' ? null : s;
  };
  const num = (v) => {
    const s = (v ?? '').toString().trim();
    return s === '' ? null : Number(s);
  };
  return {
    name: (form.name || '').trim(),
    code: text(form.code),
    address: text(form.address),
    city: text(form.city),
    district: text(form.district),
    postal_code: text(form.postal_code),
    country_code: form.country_code || 'TR',
    latitude: num(form.latitude),
    longitude: num(form.longitude),
    geo_radius_meters: num(form.geo_radius_meters) ?? 100,
    timezone: form.timezone || 'Europe/Istanbul',
    manager_user_id: text(form.manager_user_id),
    phone: text(form.phone),
    email: text(form.email),
  };
}

// ── Bölüm başlığı ─────────────────────────────────────────────────────
function SectionTitle({ children }) {
  return (
    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{children}</p>
  );
}

// ── Form alanları (create + edit ortak) ───────────────────────────────

/**
 * Yeniden kullanılır şube form alanları. State + submit BranchFormPage'de.
 *
 * @param {object}   form      form state
 * @param {object}   errors    alan-bazlı hata objesi
 * @param {Function} onChange  (key) => (event) => void  — parent set helper'ı
 * @param {object[]} managers  yönetici dropdown için aktif kullanıcılar
 */
export function BranchFormFields({ form, errors, onChange, managers = [] }) {
  return (
    <div className="space-y-6">
      {/* 1 — Temel */}
      <div className="space-y-3">
        <SectionTitle>Temel Bilgiler</SectionTitle>
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Şube Adı *"
            value={form.name}
            onChange={onChange('name')}
            error={errors.name}
            placeholder="Merkez Şube"
          />
          <Input
            label="Şube Kodu"
            value={form.code}
            onChange={onChange('code')}
            error={errors.code}
            placeholder="MERKEZ (opsiyonel)"
          />
        </div>
      </div>

      {/* 2 — Konum */}
      <div className="space-y-3">
        <SectionTitle>Konum</SectionTitle>
        <Textarea label="Adres" value={form.address} onChange={onChange('address')} rows={2} />
        <div className="grid grid-cols-3 gap-4">
          <Input label="Şehir" value={form.city} onChange={onChange('city')} />
          <Input label="İlçe" value={form.district} onChange={onChange('district')} />
          <Input label="Posta Kodu" value={form.postal_code} onChange={onChange('postal_code')} />
        </div>
        <Input label="Ülke" value="Türkiye" disabled />
      </div>

      {/* 3 — GPS */}
      <div className="space-y-3">
        <SectionTitle>GPS Konumu</SectionTitle>
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Enlem (latitude)"
            type="number"
            step="any"
            value={form.latitude}
            onChange={onChange('latitude')}
            error={errors.latitude}
            placeholder="41.0082"
          />
          <Input
            label="Boylam (longitude)"
            type="number"
            step="any"
            value={form.longitude}
            onChange={onChange('longitude')}
            error={errors.longitude}
            placeholder="28.9784"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-slate-700">
            Coğrafi Yarıçap: {form.geo_radius_meters || 100} m
          </label>
          <input
            type="range"
            min="10"
            max="5000"
            step="10"
            value={form.geo_radius_meters || 100}
            onChange={onChange('geo_radius_meters')}
            className="w-full accent-blue-600"
          />
          {errors.geo_radius_meters && (
            <p className="text-xs text-red-500">{errors.geo_radius_meters}</p>
          )}
        </div>
        <BranchMapPreview
          latitude={form.latitude}
          longitude={form.longitude}
          radius={form.geo_radius_meters}
        />
      </div>

      {/* 4 — Saat dilimi */}
      <div className="space-y-3">
        <SectionTitle>Saat Dilimi</SectionTitle>
        <Select label="Saat Dilimi" value={form.timezone} onChange={onChange('timezone')} error={errors.timezone}>
          <optgroup label="Yaygın">
            {COMMON_TZ.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
          </optgroup>
          <optgroup label="Tümü">
            {ALL_TZ.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
          </optgroup>
        </Select>
      </div>

      {/* 5 — Yönetici */}
      <div className="space-y-3">
        <SectionTitle>Yönetici</SectionTitle>
        <Select
          label="Şube Yöneticisi"
          value={form.manager_user_id}
          onChange={onChange('manager_user_id')}
          error={errors.manager_user_id}
        >
          <option value="">— Yönetici yok —</option>
          {managers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </Select>
      </div>

      {/* 6 — İletişim */}
      <div className="space-y-3">
        <SectionTitle>İletişim</SectionTitle>
        <div className="grid grid-cols-2 gap-4">
          <Input label="Telefon" value={form.phone} onChange={onChange('phone')} error={errors.phone} />
          <Input
            label="E-posta"
            type="email"
            value={form.email}
            onChange={onChange('email')}
            error={errors.email}
            placeholder="sube@firma.com"
          />
        </div>
      </div>
    </div>
  );
}
